import { useState } from "react";
import {
  useUploadExtractedText,
  type ExtractedTextView,
} from "../api/hooks/useUploadExtractedText";

interface Props {
  uploadId: string;
  // The BE's ``has_extracted_text`` flag - whether raw text was kept for this
  // upload. Renders nothing when falsy (non-source-text kinds, or rows
  // predating the feature). Passed RAW (true / false / null / undefined).
  hasExtractedText?: boolean | null;
}

// A note explaining when the parse stopped early / how the text was read, so a
// partial or OCR read is visible rather than silently looking complete.
//
// ``display_truncated`` takes precedence because it is the only case where the
// EXTRACTION is complete and only this panel's view is clipped - reading a
// stop_reason note there would wrongly suggest content was never extracted.
const stopReasonNote = (v: ExtractedTextView): string | null => {
  if (v.display_truncated) {
    return (
      `Only the first ${v.char_count.toLocaleString()} characters are shown here - ` +
      "this panel is capped. The full text was extracted and is what the agents read."
    );
  }
  switch (v.stop_reason) {
    case "appendix":
      return (
        "The read stopped at the appendix - appendix material (datasheets, " +
        "calculations, scanned annexes) was not included."
      );
    case "page_budget":
      return (
        `Large document: only the first ${v.pages_parsed ?? "?"} of ` +
        `${v.total_pages ?? "?"} pages were read; later pages were not.`
      );
    case "char_budget":
      return (
        `Very long document: only the first ${v.char_count.toLocaleString()} ` +
        "characters were read; the remainder was beyond the extraction budget."
      );
    case "ocr":
    case "unreadable":
    case "complete":
      return null;
  }
};

// The BODY of the panel's "Text" section - exactly what a read_document on this
// file returns to an agent. The header, the title and the expand/collapse live in
// the enclosing CollapsibleSection, so this renders content only.
//
// Fetched lazily off the Postgres hot path (it can be large): the section is
// collapsed by default, and mounting only happens on expand, so a closed section
// costs nothing. Admin-only customer data.
export const ExtractedTextSection = ({
  uploadId,
  hasExtractedText,
}: Props): JSX.Element | null => {
  const [copied, setCopied] = useState(false);
  const { data, isLoading, isError, error } = useUploadExtractedText(uploadId);

  if (!hasExtractedText) return null;

  const onCopy = async () => {
    if (!data?.text) return;
    try {
      await navigator.clipboard.writeText(data.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      window.alert("Could not copy to clipboard in this browser.");
    }
  };

  const note = data ? stopReasonNote(data) : null;
  const isOcr = data?.stop_reason === "ocr";
  const isUnreadable = data?.stop_reason === "unreadable";
  const hasText = !!data && data.text.length > 0;

  return (
    <div style={{ padding: 12 }}>
      {data?.text ? (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
          <button
            type="button"
            onClick={onCopy}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 4,
              padding: "2px 10px",
              fontSize: 12,
              background: "white",
              cursor: "pointer",
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      ) : null}
      {isLoading ? (
            <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
              Loading extracted text…
            </p>
          ) : isError ? (
            <div style={{ fontSize: 13, color: "#b91c1c" }}>
              Could not load the extracted text (
              {error instanceof Error ? error.message : "the request failed"}). Try
              again in a moment.
            </div>
          ) : !data ? (
            <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
              No extracted text was kept for this file.
            </p>
          ) : (
            <>
              {note ? (
                <div
                  style={{
                    padding: 10,
                    marginBottom: 10,
                    fontSize: 13,
                    color: "#1e3a8a",
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: 6,
                  }}
                >
                  {note}
                </div>
              ) : null}
              {isOcr && hasText ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 8,
                    fontStyle: "italic",
                  }}
                >
                  Read via OCR over {data.ocr_image_count} page image(s) - the
                  PDF had no machine-readable text layer.
                </div>
              ) : null}
              {isUnreadable ? (
                <div style={{ fontSize: 13, color: "#b91c1c" }}>
                  This PDF could not be read - it has no machine-readable text
                  layer and its pages could not be rendered. Open the file
                  itself, or re-upload a non-corrupt copy.
                </div>
              ) : hasText ? (
                <pre
                  style={{
                    margin: 0,
                    maxHeight: 360,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    lineHeight: 1.5,
                    color: "#111827",
                  }}
                >
                  {data.text}
                </pre>
              ) : isOcr ? (
                <div style={{ fontSize: 13, color: "#6b7280" }}>
                  This document was read via OCR but no transcription was
                  captured - open the file itself to read it.
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#6b7280" }}>
                  No readable text was captured for this document - open the file
                  itself to read it.
                </div>
              )}
            </>
          )}
    </div>
  );
};

export default ExtractedTextSection;
